import React, { useCallback, useMemo } from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Box, Flex } from '@chakra-ui/react'
import useFetchOptionsByFilter from '../../../services/useFetchOptionsByFilter'
import { ColumnResizeHandler } from './ColumnResizeHandler'
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons'
import Rating from '../../ui/Rating'
import { observer } from 'mobx-react'
import rootStore from '../../../store/RootStore'
import { useNavigate } from 'react-router-dom'

const OptionsTable = observer(() => {
  const {
    optionsTableStore: { optionsTable },
    reportStore: { reportSearchText },
  } = rootStore

  const { isLoading, error } = useFetchOptionsByFilter()

  const columnHelper = createColumnHelper()

  const defaultColumns = useMemo(
    () =>
      optionsTable && optionsTable.length > 0
        ? [
            columnHelper.group({
              header: '',
              id: 'rowHeader',
              size: 300,
              minSize: 150,
              columns: [
                columnHelper.accessor('header', {
                  header: optionsTable[0].ColumnNameForRowHeader,
                  cell: props => props.getValue(),
                  size: 300,
                  minSize: 150,
                }),
              ],
            }),
            ...optionsTable[0].Columns.Columns.map((column, index) =>
              columnHelper.group({
                header: column.Header,
                columns: column.Columns.map(col =>
                  columnHelper.accessor(col.Id, {
                    header: col.Header,
                    cell: props => props.getValue(),
                    decimals: col.UnitType.DecimalPlaces,
                    bgColor: index % 2 === 0 ? 'white' : '#f5f5f5',
                    size: 100,
                    minSize: 60,
                  }),
                ),
              }),
            ),
          ]
        : [],
    [columnHelper, optionsTable],
  )

  const tableData = useMemo(() => {
    if (!optionsTable || optionsTable.length === 0) return []
    const rows = []

    Object.values(optionsTable[0].RowsById).forEach(row => {
      const obj = {
        chartId: row.chartId,
        header: row.Header,
      }

      let counter = 0

      defaultColumns?.forEach((column, index) => {
        if (index === 0) return
        column.columns.forEach(col => {
          const number = parseFloat(row.Cells[counter]?.Value)
          obj[col.accessorKey] = isNaN(number)
            ? '-'
            : number.toFixed(col.decimals)
          counter++
        })
      })

      if (reportSearchText.length > 0) {
        const isMatch = Object.values(obj).some(value =>
          value.toLowerCase().includes(reportSearchText.toLowerCase()),
        )
        if (!isMatch) return
      }

      rows.push(obj)
    })
    return rows
  }, [reportSearchText, optionsTable, defaultColumns])

  if (isLoading) return <Box>Loading...</Box>
  if (error) return <Box>Error</Box>
  if (!(tableData && tableData.length > 0)) return <Box>No data</Box>

  return (
    <Table
      optionsTable={tableData}
      defaultColumns={defaultColumns}
    />
  )
})

const Table = ({ optionsTable = [], defaultColumns }) => {
  const [columns] = React.useState(() => [...defaultColumns])
  const table = useReactTable({
    data: optionsTable,
    columns,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <Box
      position="relative"
      maxWidth="100%"
      zIndex={0}
      maxHeight="calc(100vh - 180px)"
      overflowY="auto"
      overflowX="auto">
      <Box w={table.getTotalSize()}>
        <TableHeaderGroups
          headerGroups={table.getHeaderGroups()}
          table={table}
        />
        <TableData rows={table.getRowModel().rows} />
      </Box>
    </Box>
  )
}

const TableHeaderGroups = ({ headerGroups = [] }) => {
  return (
    <Flex
      position="sticky"
      top={0}
      flexDir="column"
      bg="#f5f5f5"
      zIndex={10}
      className="thead"
      boxShadow={'3px 0 3px rgba(0, 0, 0, .1)'}>
      {headerGroups.map((headerGroup, rowIndex) => (
        <Flex
          position="relative"
          key={headerGroup.id}
          className="tr"
          _first={{
            '.th': {
              textTransform: 'uppercase',
              '&:nth-of-type(even)': {
                backgroundColor: 'white',
              },
              '&:nth-of-type(odd)': {
                backgroundColor: '#f5f5f5',
              },
            },
          }}>
          {headerGroup.headers.map(header => (
            <Box
              key={header.id}
              {...cellProps}
              width={`${header.getSize()}px`}
              className="th"
              fontWeight="bold"
              padding="8px 16px"
              backgroundColor={header.column.columnDef?.bgColor}
              _first={{
                top: 0,
                position: 'sticky',
                left: 0,
                zIndex: 999,
                textTransform: 'uppercase',
                '&:nth-of-type(even)': {
                  backgroundColor: 'white',
                },
                '&:nth-of-type(odd)': {
                  backgroundColor: '#f5f5f5',
                },
              }}>
              <Box
                h="50px"
                display="flex"
                alignItems="center"
                justifyContent="center">
                {header.isPlaceholder ? null : (
                  <Box
                    display="flex"
                    flexDir="column"
                    alignItems="center"
                    justifyContent="flex-end"
                    userSelect="none"
                    cursor="pointer"
                    className={
                      header.column.getCanSort()
                        ? 'cursor-pointer select-none'
                        : ''
                    }
                    onClick={header.column.getToggleSortingHandler()}>
                    {{
                      asc: (
                        <TriangleDownIcon
                          fontSize="8px"
                          color="brand.textDarkBlue"
                        />
                      ),
                      desc: (
                        <TriangleUpIcon
                          fontSize="8px"
                          color="brand.textDarkBlue"
                        />
                      ),
                    }[header.column.getIsSorted()] ?? null}
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {rowIndex === 0 && <ColumnResizeHandler header={header} />}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Flex>
      ))}
    </Flex>
  )
}

const TableData = ({ rows = [] }) => {
  const navigate = useNavigate()

  const handleTableNavigation = useCallback(
    row => {
      navigate('/reports', { state: { chartId: row.original.chartId } })
    },
    [navigate],
  )

  return (
    <Box className="tbody">
      {rows.map(row => (
        <Flex
          key={row.id}
          h="40px"
          className="tr"
          _hover={{
            cursor: 'pointer',
          }}
          onClick={() => handleTableNavigation(row)}>
          {row.getVisibleCells().map((cell, index) => (
            <Box
              key={cell.id}
              w={`${cell.column.getSize()}px`}
              position={index === 0 ? 'sticky' : 'relative'}
              left={index === 0 ? 0 : null}
              {...cellProps}
              className="td"
              backgroundColor={cell.column.columnDef.bgColor}
              display="flex"
              alignItems="center"
              textAlign="center"
              justifyContent="center"
              _first={{
                paddingLeft: '32px',
                position: 'sticky',
                left: 0,
                right: 0,
                zIndex: 9,
                justifyContent: 'flex-start',
                textAlign: 'left',
                backgroundColor: '#f5f5f5',
              }}>
              {(cell.column?.id?.includes('Rank') && (
                <Rating value={cell.getValue()} />
              )) ||
                flexRender(cell.column.columnDef.cell, cell.getContext())}
            </Box>
          ))}
        </Flex>
      ))}
    </Box>
  )
}

const cellProps = {
  margin: 0,
  position: 'relative',
  padding: '0.5rem',
  fontSize: '12px',
  color: 'brand.textDarkBlue',
  alignItems: 'center',
  textAlign: 'center',
  _last: {
    borderRight: 0,
    boxShadow: 'none',
  },
}

OptionsTable.propTypes = {}

export default OptionsTable
